import 'helpers/rum/initDatadogRum';
import 'helpers/logger/initLogger';

import { Authenticator } from '@aws-amplify/ui-react';
import { cx } from '@styled/css';
import { scrollbar } from '@styled/recipes';
import { AllEnterpriseModule, LicenseManager, ModuleRegistry } from 'ag-grid-enterprise';
import { Amplify, Hub } from 'aws-amplify';
import React from 'react';
import { createRoot } from 'react-dom/client';

import './index.css';
import './styles/legacy.scss';

import { dismissToast, ToastsProvider } from 'components/Toast/Toast';
import { ModalContainer } from 'helpers/react-modal-promise/ModalContainer';
import { AG_GRID_LICENSE } from 'state_management/env_constants';
import { clearLocalServiceOverrides } from 'state_management/localServiceOverrides';

import App from './App';
import { awsmobile } from './aws-exports';
import reportWebVitals from './reportWebVitals';
// Apply global scrollbar to body
document.body.className = cx(document.body.className, scrollbar({ thickness: 'thick' }));
// pdf node server typings
type serverReadyParams = {
  format: string;
  printBackground: boolean;
  landscape: boolean;
  headerTemplate?: string;
  displayHeaderFooter?: boolean;
  margin?: { top?: string; bottom?: string; left?: string; right?: string };
};
declare global {
  interface Window {
    serverReady?: (args: serverReadyParams) => Promise<void>;
    generatePDF?: (scrapeIndex: number, hasFooter: boolean, footerTemplate: string) => Promise<void>;
    closePDF?: () => Promise<void>;
  }
}

ModuleRegistry.registerModules([AllEnterpriseModule]);

LicenseManager.setLicenseKey(AG_GRID_LICENSE);

Amplify.configure(awsmobile);

// Global logout cleanup (this listener sits outside the auth tree, so it runs for every logout
// path). Ensure no persistent toasts survive logout/login cycles, and clear any local/localhost
// service endpoint overrides so a fresh session starts from the remote defaults.
Hub.listen('auth', (capsule) => {
  const event = capsule?.payload?.event ?? '';
  if (event === 'signOut' || event === 'signedOut') {
    dismissToast();
    const hadLocalOverrides = clearLocalServiceOverrides();
    if (hadLocalOverrides) {
      // Module-level singletons (e.g. the Liveblocks realtime client) captured the local endpoints
      // at load time, so a full reload to the root is required to rebuild them from the cleared,
      // remote-default localStorage. Remote-only sessions skip this and keep the fast SPA logout.
      window.location.assign('/');
    }
  }
});

const root = document.getElementById('root') as HTMLElement;
createRoot(root).render(
  <React.StrictMode>
    <ToastsProvider>
      <Authenticator.Provider>
        <App />
      </Authenticator.Provider>
    </ToastsProvider>

    <ModalContainer scope="root" />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
